import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { bakeryInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';

const ModernHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', label: t.nav?.home || 'الرئيسية' },
    { path: '/products', label: t.nav?.products || 'منتجاتنا' },
    { path: '/about', label: t.nav?.about || 'من نحن' },
    { path: '/contact', label: t.nav?.contact || 'تواصل معنا' }
  ];

  return (
    <header
      className={`fixed top-0 start-0 end-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-[#faf8f5]/95 backdrop-blur-xl shadow-sm border-b border-[#b89759]/10' 
          : 'bg-gradient-to-b from-black/30 to-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => navigate('/')}
            data-testid="logo-link"
          >
            <div className="relative">
              <img 
                src={bakeryInfo.logo} 
                alt="Dura Alsham"
                className="w-12 h-12 lg:w-14 lg:h-14 object-contain transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className={isRTL ? 'text-end' : 'text-start'}>
              <h1 
                className="font-heading text-xl lg:text-2xl font-bold transition-colors duration-300"
                style={{ 
                  color: isScrolled ? '#0d4023' : 'white',
                  textShadow: isScrolled ? 'none' : '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                Dura Alsham
              </h1>
              <p 
                className="text-xs transition-colors duration-300 hidden sm:block"
                style={{ color: isScrolled ? '#5a7062' : 'rgba(255,255,255,0.8)' }}
              >
                {t.hero?.tagline || 'Authentic Levantine Bread'}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full relative ${
                  isActive(item.path) 
                    ? isScrolled 
                      ? 'text-[#0d4023] bg-[#0d4023]/5' 
                      : 'text-white bg-white/10'
                    : isScrolled
                      ? 'text-[#5a7062] hover:text-[#0d4023] hover:bg-[#0d4023]/5'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <div className="hidden sm:block">
              <LanguageSelector isScrolled={isScrolled} />
            </div>
            
            {/* Login Button */}
            <button
              onClick={() => navigate('/login')}
              data-testid="login-btn"
              className={`hidden lg:flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-sm transition-colors duration-300 ${
                isScrolled
                  ? 'bg-[#0d4023] text-[#faf8f5] hover:bg-[#1a5c32]'
                  : 'bg-[#b89759] text-[#0d4023] hover:bg-[#cba660]'
              }`}
            >
              {t.nav?.login || 'تسجيل الدخول'}
            </button>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-full transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              style={{ 
                color: isScrolled ? '#0d4023' : 'white',
                backgroundColor: isScrolled ? 'rgba(13, 64, 35, 0.05)' : 'rgba(255,255,255,0.1)'
              }}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className={`pb-6 space-y-1 border-t pt-4 ${isScrolled ? 'border-[#b89759]/10' : 'border-white/10'}`}>
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setIsMobileMenuOpen(false);
                }}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                className={`block w-full py-3 px-4 text-base font-medium transition-colors rounded-xl ${isRTL ? 'text-end' : 'text-start'} ${
                  isActive(item.path)
                    ? isScrolled 
                      ? 'bg-[#0d4023]/5 text-[#0d4023]' 
                      : 'bg-white/10 text-white'
                    : isScrolled
                      ? 'text-[#5a7062] hover:bg-[#0d4023]/5 hover:text-[#0d4023]'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* Mobile Login Button */}
            <button
              onClick={() => {
                navigate('/login');
                setIsMobileMenuOpen(false);
              }}
              data-testid="mobile-login-btn"
              className={`w-full mt-4 py-3 px-4 rounded-xl font-medium text-center transition-colors ${
                isScrolled
                  ? 'bg-[#0d4023] text-[#faf8f5]'
                  : 'bg-[#b89759] text-[#0d4023]'
              }`}
            >
              {t.nav?.login || 'تسجيل الدخول'}
            </button>
            
            {/* Mobile Language Selector */}
            <div className="pt-4 sm:hidden">
              <LanguageSelector isScrolled={isScrolled} />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
